import { isNavigationEntryShown } from '@brand-lolly/app/shared/navigation/navigation-entry-visibility';
import { NAVIGATION_PRODUCT_ACCESS } from '@brand-lolly/app/shared/navigation/navigation-product-access';
import {
	buildRoute,
	matchRouteTemplate,
	parseRouteTemplate,
	stripQueryAndFragment
} from '@brand-lolly/app/_services/ana/route-template';
import {
	SCREEN_CONTRACT,
	SCREEN_SETTINGS
} from '@brand-lolly/app/_services/ana/screen-contract.generated';

import {
	UniversalSearchPage,
	UniversalSearchSetting,
	UniversalSearchVocabulary
} from './universal-search.model';

const MAX_PAGES = 6;
/** How many settings the group lists before it says how many more match. */
export const MAX_SETTINGS = 6;

/** Lower-cased words with diacritics and punctuation gone. */
export function searchWords(text: string): string[] {
	return text
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.split(/[^a-z0-9]+/)
		.filter(word => word.length > 0);
}

/** The rail entry an address belongs to: the longest route it starts under. */
function entryFor(address: string) {
	let best: typeof NAVIGATION_PRODUCT_ACCESS[number] | null = null;
	for (const row of NAVIGATION_PRODUCT_ACCESS) {
		const under =
			row.route === '/'
				? address === '/'
				: address === row.route || address.startsWith(row.route + '/');
		if (under && (!best || row.route.length > best.route.length)) {
			best = row;
		}
	}
	return best;
}

/**
 * Every page the search can lead to: a screen's `href`, or its route when that
 * names no record and no wildcard — the same rule the backend's navigation
 * guard and `verify-ana-contract` apply. A department this build leaves out of
 * the rail contributes nothing; one the brand does not hold stays, because its
 * route answers with that department's own explaining page.
 */
export function searchablePages(): UniversalSearchPage[] {
	const pages: UniversalSearchPage[] = [];
	const seen = new Set<string>();
	for (const screen of Object.values(SCREEN_CONTRACT)) {
		const address =
			screen.href ??
			(screen.route.includes(':') || screen.route.includes('*')
				? null
				: screen.route);
		if (!address || seen.has(address)) continue;
		const entry = entryFor(address);
		if (entry && !isNavigationEntryShown(entry.entry)) continue;
		seen.add(address);
		pages.push({
			id: screen.id,
			title: screen.title,
			department: screen.department,
			address,
			titleWords: searchWords(screen.title),
			termWords: screen.searchTerms.map(searchWords),
			terms: screen.searchTerms
		});
	}
	return pages;
}

// What a record is called in "Ana asks which …", by the segment before its id.
const RECORD_NOUNS: Record<string, string> = {
	campaigns: 'campaign',
	setup: 'campaign',
	'my-brand': 'brand',
	competitors: 'competitor project',
	industry: 'industry'
};

// CONTRACT: the backend's screen contract declares the AI summary settings on
// record kind ai_summary_config — one config per SL brand, competitor project or
// industry — and the page opens on the record `?entity_type=&entity_id=` names.
const AI_SUMMARY_ADDRESS = '/social-listening/ai-summary';
const ENTITY_TYPE_PARAM = 'entity_type';
const ENTITY_ID_PARAM = 'entity_id';
const LISTENING_RECORDS: readonly {
	readonly template: string;
	readonly entityType: string;
}[] = [
	{ template: '/social-listening/my-brand/:id<uuid>/*', entityType: 'brand' },
	{
		template: '/social-listening/competitors/:id<uuid>/*',
		entityType: 'competitor_project'
	},
	{
		template: '/social-listening/industry/:id<uuid>/*',
		entityType: 'industry'
	}
];

/**
 * Every declared setting of a department this build shows in the rail. A
 * setting on a record's page (a campaign, an SL brand) has a record template:
 * its address up to the record's id, e.g. `/campaigns/:projectId/*`.
 */
export function searchableSettings(): UniversalSearchSetting[] {
	const settings: UniversalSearchSetting[] = [];
	for (const setting of Object.values(SCREEN_SETTINGS)) {
		const address = setting.address;
		const entry = entryFor(address);
		if (entry && !isNavigationEntryShown(entry.entry)) continue;
		const segments = parseRouteTemplate(address).segments;
		let lastParam = -1;
		segments.forEach((segment, index) => {
			if (segment.param) lastParam = index;
		});
		const recordTemplate =
			lastParam === -1
				? null
				: '/' +
				  segments
						.slice(0, lastParam + 1)
						.map(segment =>
							segment.literal !== null
								? segment.literal
								: segment.paramType === 'any'
								? `:${segment.param}`
								: `:${segment.param}<${segment.paramType}>`
						)
						.join('/') +
				  '/*';
		const listeningRecord =
			stripQueryAndFragment(address) === AI_SUMMARY_ADDRESS;
		settings.push({
			key: setting.key,
			label: setting.label,
			department: setting.department,
			screenTitle: SCREEN_CONTRACT[setting.screen]?.title ?? '',
			address,
			recordTemplate,
			recordNoun: listeningRecord
				? 'brand'
				: lastParam > 0
				? RECORD_NOUNS[segments[lastParam - 1].literal ?? ''] ?? 'one'
				: null,
			listeningRecord,
			writable: setting.writable,
			titleWords: searchWords(setting.label),
			termWords: setting.terms.map(searchWords),
			terms: setting.terms
		});
	}
	return settings;
}

/**
 * Where following `setting` leads from the page at `currentUrl`: its address,
 * with the record ids filled from the current page when that page is a record
 * of the same kind — null when the setting needs a record nobody is on.
 */
export function settingAddress(
	setting: UniversalSearchSetting,
	currentUrl: string
): string | null {
	const query = setting.address.slice(
		stripQueryAndFragment(setting.address).length
	);
	if (setting.listeningRecord) return listeningRecordAddress(currentUrl);
	if (!setting.recordTemplate) return buildRoute(setting.address) + query;
	const params = matchRouteTemplate(setting.recordTemplate, currentUrl);
	if (!params || namesALiteralScreen(setting.recordTemplate, currentUrl)) {
		return null;
	}
	return buildRoute(setting.address, params) + query;
}

/**
 * The AI summary of the SL record on screen — an SL brand's, competitor
 * project's or industry's page, or the AI summary already open on one — or
 * null when no SL record is on screen.
 */
function listeningRecordAddress(currentUrl: string): string | null {
	const path = stripQueryAndFragment(currentUrl);
	let entityType: string | null = null;
	let entityId: string | null = null;
	if (path === AI_SUMMARY_ADDRESS) {
		const params = new URLSearchParams(
			currentUrl.slice(path.length).split('#')[0]
		);
		entityType = params.get(ENTITY_TYPE_PARAM);
		entityId = params.get(ENTITY_ID_PARAM);
	} else {
		for (const record of LISTENING_RECORDS) {
			const params = matchRouteTemplate(record.template, currentUrl);
			if (params) {
				entityType = record.entityType;
				entityId = params['id'];
				break;
			}
		}
	}
	if (!entityType || !entityId) return null;
	const query = new URLSearchParams({
		[ENTITY_TYPE_PARAM]: entityType,
		[ENTITY_ID_PARAM]: entityId
	});
	return `${AI_SUMMARY_ADDRESS}?${query.toString()}`;
}

/**
 * True when the id the record template bound is a declared screen's literal
 * segment instead — `/campaigns/first-time` is a page, not campaign "first-time".
 */
function namesALiteralScreen(recordTemplate: string, currentUrl: string) {
	const depth = parseRouteTemplate(recordTemplate).segments.length;
	const parts = stripQueryAndFragment(currentUrl)
		.split('/')
		.filter(part => part !== '')
		.slice(0, depth);
	return Object.values(SCREEN_CONTRACT).some(screen => {
		const segments = parseRouteTemplate(screen.route).segments;
		return (
			segments.length >= depth &&
			parts.every((part, index) => segments[index].literal === part)
		);
	});
}

function coveredBy(queryWord: string, words: readonly string[]): boolean {
	return words.some(word => word.startsWith(queryWord));
}

/**
 * Pages whose title or terms hold every query word as a word prefix, best
 * first: all words in the title, then a match through the terms; then the
 * title with fewer words left unmatched; then declaration order.
 */
export function matchPages<T extends UniversalSearchVocabulary>(
	query: string,
	pages: readonly T[]
): T[] {
	const queryWords = searchWords(query);
	if (queryWords.length === 0) return [];
	const scored: {
		page: T;
		rank: number;
		rest: number;
		order: number;
	}[] = [];
	pages.forEach((page, order) => {
		const vocabulary = [...page.titleWords, ...page.termWords.flat()];
		if (!queryWords.every(word => coveredBy(word, vocabulary))) return;
		const inTitle = queryWords.every(word =>
			coveredBy(word, page.titleWords)
		);
		const rest = page.titleWords.filter(
			word => !queryWords.some(queryWord => word.startsWith(queryWord))
		).length;
		scored.push({ page, rank: inTitle ? 0 : 1, rest, order });
	});
	scored.sort(
		(a, b) => a.rank - b.rank || a.rest - b.rest || a.order - b.order
	);
	return scored.slice(0, MAX_PAGES).map(entry => entry.page);
}

// CONTRACT: the scoring constants and stop words of the backend's setting
// resolver, app/agentic_ai/assistant/service/settings/resolver.py — Ana and the
// search must put the same setting first for the same words.
const LABEL_WORD_SCORE = 2;
const TERM_WORD_SCORE = 1;
const PHRASE_BONUS = 3;
// TRAP: direction words score nothing on their own — they sit in the terms of
// nearly every switch. They still count inside a phrase hit.
const STOP_WORDS = new Set([
	'a',
	'an',
	'the',
	'my',
	'our',
	'me',
	'i',
	'we',
	'it',
	'its',
	'this',
	'that',
	'to',
	'of',
	'for',
	'from',
	'in',
	'on',
	'off',
	'at',
	'by',
	'with',
	'and',
	'or',
	'please',
	'can',
	'you',
	'do',
	'dont',
	'want',
	'all',
	'any',
	'some',
	'turn',
	'switch',
	'stop',
	'start',
	'pause',
	'resume',
	'enable',
	'disable',
	'activate',
	'deactivate'
]);

/** The resolver's plural fold: "emails" is typed for "email". */
function stem(word: string): string {
	return word.length > 3 && word.endsWith('s') && !word.endsWith('ss')
		? word.slice(0, -1)
		: word;
}

/** True when `queryWord` starts a word of `words`, plurals folded. */
function prefixOf(queryWord: string, words: readonly string[]): boolean {
	const stemmed = stem(queryWord);
	return words.some(
		word => word.startsWith(queryWord) || stem(word).startsWith(stemmed)
	);
}

/**
 * A phrase hit either way round: the typed words, in order and adjacent,
 * inside `words` ("digest" in "digest email") — or all of `words` inside what
 * was typed, the resolver's direction ("stop the digest now").
 */
function phraseHit(
	queryWords: readonly string[],
	words: readonly string[]
): boolean {
	const runsInside = (
		needle: readonly string[],
		hay: readonly string[],
		matches: (part: string, whole: string) => boolean
	) => {
		for (let start = 0; start + needle.length <= hay.length; start++) {
			if (
				needle.every((part, offset) =>
					matches(part, hay[start + offset])
				)
			) {
				return true;
			}
		}
		return false;
	};
	if (!words.length) return false;
	return (
		runsInside(queryWords, words, (typed, declared) =>
			prefixOf(typed, [declared])
		) ||
		runsInside(
			words,
			queryWords,
			(declared, typed) => stem(declared) === stem(typed)
		)
	);
}

/**
 * The index of the term sharing the most query words — content words first,
 * so "stop emails" shows an email term, not a "stop" one — or -1.
 */
function bestTerm(
	queryWords: readonly string[],
	setting: UniversalSearchVocabulary
): number {
	let best = -1;
	let bestShare = 0;
	setting.termWords.forEach((words, index) => {
		const covered = queryWords.filter(word => prefixOf(word, words));
		const share =
			covered.filter(word => !STOP_WORDS.has(word)).length * 10 +
			covered.length;
		if (share > bestShare) {
			best = index;
			bestShare = share;
		}
	});
	return best;
}

/** One match, and the declared term it was found through (null for the label). */
export interface SettingMatch<T> {
	readonly item: T;
	readonly term: string | null;
}

/**
 * Settings whose label or terms hold every query word as a word prefix, best
 * first by the resolver's score — a query word in the label 2, only in the
 * terms 1, a phrase hit in the label or one term 3 more — then a `preferred`
 * setting (the one of the record on screen), then the one with the most terms
 * sharing a query word (the setting the symptom describes), then declaration
 * order. Not
 * capped: the caller says how many more there are.
 */
export function rankSettings<T extends UniversalSearchVocabulary>(
	query: string,
	settings: readonly T[],
	preferred: (setting: T) => boolean = () => false
): SettingMatch<T>[] {
	const queryWords = searchWords(query);
	if (queryWords.length === 0) return [];
	const scored: {
		match: SettingMatch<T>;
		score: number;
		preferred: number;
		terms: number;
		order: number;
	}[] = [];
	settings.forEach((setting, order) => {
		const termWords = setting.termWords.flat();
		const vocabulary = [...setting.titleWords, ...termWords];
		if (!queryWords.every(word => prefixOf(word, vocabulary))) return;
		let score = 0;
		for (const word of queryWords) {
			if (STOP_WORDS.has(word)) continue;
			if (prefixOf(word, setting.titleWords)) score += LABEL_WORD_SCORE;
			else if (prefixOf(word, termWords)) score += TERM_WORD_SCORE;
		}
		const phraseTerm = setting.termWords.findIndex(words =>
			phraseHit(queryWords, words)
		);
		if (phraseHit(queryWords, setting.titleWords) || phraseTerm !== -1) {
			score += PHRASE_BONUS;
		}
		// The term shown as the reason: only when the label alone does not match.
		let term: string | null = null;
		if (!queryWords.every(word => prefixOf(word, setting.titleWords))) {
			const index =
				phraseTerm !== -1 ? phraseTerm : bestTerm(queryWords, setting);
			term = index === -1 ? null : setting.terms[index] ?? null;
		}
		scored.push({
			match: { item: setting, term },
			score,
			preferred: preferred(setting) ? 0 : 1,
			terms: setting.termWords.filter(words =>
				queryWords.some(word => prefixOf(word, words))
			).length,
			order
		});
	});
	scored.sort(
		(a, b) =>
			b.score - a.score ||
			a.preferred - b.preferred ||
			b.terms - a.terms ||
			a.order - b.order
	);
	return scored.map(entry => entry.match);
}

export interface HighlightedPart {
	readonly text: string;
	readonly matched: boolean;
}

/** `text` cut into runs, the ones a query word is a prefix of marked. */
export function highlight(text: string, query: string): HighlightedPart[] {
	const queryWords = searchWords(query);
	if (queryWords.length === 0) return [{ text, matched: false }];
	const parts: HighlightedPart[] = [];
	for (const token of text.split(/(\s+)/)) {
		if (!token) continue;
		const word = searchWords(token)[0];
		const matchedWord = word
			? queryWords.find(queryWord => word.startsWith(queryWord))
			: undefined;
		if (!matchedWord) {
			parts.push({ text: token, matched: false });
			continue;
		}
		// Mark only the prefix that was typed, keeping any leading punctuation.
		const start = token.toLowerCase().indexOf(matchedWord[0]);
		const end = Math.min(token.length, start + matchedWord.length);
		if (start > 0)
			parts.push({ text: token.slice(0, start), matched: false });
		parts.push({ text: token.slice(start, end), matched: true });
		if (end < token.length)
			parts.push({ text: token.slice(end), matched: false });
	}
	return parts;
}
